import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { ApiError } from '../api/client';
import { ArcPage, ArcSelect, ArcSummaryGrid, ArcSummaryMetric, ArcToolbar } from '../components/arc/primitives/arc-primitives';
import { listLeads, setLeadStatus, type LeadListItem, type LeadStatus, type LeadKind } from '../api/crm';
import { useAuth } from '../auth/AuthContext';
import { stagesForSlug } from './crm-stages';
import { CZK_PER_EUR, czkFirst, money, moneyAlt, moneyUnit } from './crm-money';
import { dealValues } from './crm-flow';
import { LANGS, langDef } from './crm-lang';
import './crm-pipeline.css';
import './crm-redesign.css';

function initials(first: string, last: string) {
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || '?';
}
function fmtDate(s: string) {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export function CrmPipelinePage({ onOpenDetail }: { onOpenDetail: (id: string) => void }) {
  const { me } = useAuth();
  const spaceSlug = me?.activeSpaceSlug ?? null;
  const stages = stagesForSlug(spaceSlug);
  const showLangFilter = spaceSlug === 'affilmaster';
  const showKindFilter = spaceSlug === 'affilmaster';
  const stageKeys = useMemo(() => new Set(stages.map((s) => s.key)), [stages]);

  const [kindFilter, setKindFilter] = useState<'all' | LeadKind>('all');
  const [langFilter, setLangFilter] = useState<'all' | string>('all');
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  const [dropCol, setDropCol] = useState<LeadStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listLeads({
      limit: 200,
      kind: showKindFilter && kindFilter !== 'all' ? kindFilter : undefined,
      lang: showLangFilter && langFilter !== 'all' ? langFilter : undefined,
    })
      .then((res) => { if (!cancelled) { setLeads(res.items); setError(null); } })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Načtení selhalo'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [langFilter, kindFilter, showLangFilter, showKindFilter]);

  const byStage = useMemo(() => {
    const map = new Map<LeadStatus, LeadListItem[]>();
    for (const s of stages) map.set(s.key, []);
    for (const l of leads) {
      if (stageKeys.has(l.status)) map.get(l.status)!.push(l);
    }
    return map;
  }, [leads, stages, stageKeys]);
  const pipelineLeads = useMemo(() => leads.filter((lead) => stageKeys.has(lead.status)), [leads, stageKeys]);
  const deals = dealValues(pipelineLeads, spaceSlug);
  const dealMultiplier = czkFirst(spaceSlug) ? CZK_PER_EUR : 1;
  const dealSuffix = ` ${moneyUnit(spaceSlug)}`;
  const unassigned = pipelineLeads.filter((lead) => !lead.assignedToName).length;
  const boardStyle = { '--crm-stage-count': stages.length } as CSSProperties;

  function moveTo(id: number, status: LeadStatus) {
    setDropCol(null);
    setDragId(null);
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.status === status) return;
    const prev = lead.status;
    setError(null);
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    setLeadStatus(id, status).catch(() => {
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status: prev } : l)));
      setError('Přesun se nepodařilo uložit.');
    });
  }

  return (
    <ArcPage as="div" className="crm-page crm-pipeline-page">
      {loading ? (
        <div className="prototype-page-loading crm-board-loading" aria-label="Načítám pipeline"><span /><span /><span /><span /></div>
      ) : (
        <>
          <ArcSummaryGrid>
            <ArcSummaryMetric label="Příležitosti v pipeline" value={pipelineLeads.length} context="napříč všemi fázemi" />
            <ArcSummaryMetric label="Předpoklad příjmů" value={Math.round(deals.table * dealMultiplier)} suffix={` ${dealSuffix}`} context={`na stole, za měsíc${deals.table && moneyAlt(deals.table, spaceSlug) ? ` · ${moneyAlt(deals.table, spaceSlug)}` : ''}`} />
            <ArcSummaryMetric label="Potvrzené příjmy" value={Math.round(deals.closed * dealMultiplier)} suffix={` ${dealSuffix}`} context={`za měsíc${deals.closed && moneyAlt(deals.closed, spaceSlug) ? ` · ${moneyAlt(deals.closed, spaceSlug)}` : ''}`} />
            <ArcSummaryMetric label="Bez vlastníka" value={unassigned} context="čeká na přiřazení" />
          </ArcSummaryGrid>

          <ArcToolbar className="crm-board-toolbar" label="Filtry pipeline" end={<div className="crm-board-toolbar-meta"><span><strong>{pipelineLeads.length}</strong> leadů</span><small>Tažením změníte fázi</small></div>}>
            <div className="crm-board-filters">
              {showKindFilter ? (
                <ArcSelect label="Typ leadu" value={kindFilter} onChange={(value) => setKindFilter(value as 'all' | LeadKind)} options={[{ value: 'all', label: 'Všechny typy' }, { value: 'affiliate', label: 'Affiliate' }, { value: 'inzerent', label: 'Inzerent' }]} />
              ) : null}
              {showLangFilter ? (
                <ArcSelect label="Jazyk" value={langFilter} onChange={setLangFilter} options={[{ value: 'all', label: 'Všechny jazyky' }, ...LANGS.map((lang) => ({ value: lang.code, label: `${lang.flag} ${lang.label}` }))]} />
              ) : null}
            </div>
          </ArcToolbar>

          {error ? <div className="crm-board-alert" role="alert">{error}</div> : null}

          <div className="crm-board-viewport">
            <div className={`crm-board${stages.length === 3 ? ' three-stages' : ''}`} style={boardStyle}>
              {stages.map((stage, stageIndex) => {
                const items = byStage.get(stage.key) ?? [];
                const sum = items.reduce((total, lead) => total + (lead.expectedMonthlyEur ?? 0), 0);
                const stageStyle = { '--crm-stage-accent': stage.color } as CSSProperties;
                return (
                  <section
                    key={stage.key}
                    className={`crm-board-stage${dropCol === stage.key ? ' drop-active' : ''}`}
                    style={stageStyle}
                    aria-label={`${stage.label}, ${items.length} leadů`}
                    onDragOver={(event) => { event.preventDefault(); if (dropCol !== stage.key) setDropCol(stage.key); }}
                    onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setDropCol(null); }}
                    onDrop={(event) => { event.preventDefault(); const id = Number(event.dataTransfer.getData('text/plain')); if (Number.isFinite(id)) moveTo(id, stage.key); }}
                  >
                    <header className="crm-board-stage-head">
                      <div className="crm-board-stage-title"><i aria-hidden="true" /><div><small>Fáze {String(stageIndex + 1).padStart(2, '0')}</small><h2>{stage.label}</h2></div></div>
                      <span className="crm-board-stage-count">{items.length}</span>
                      <div className="crm-board-stage-value"><span>Hodnota fáze</span><strong>{sum > 0 ? money(sum, spaceSlug) : '—'}</strong>{sum > 0 && moneyAlt(sum, spaceSlug) ? <small>{moneyAlt(sum, spaceSlug)}</small> : null}</div>
                    </header>

                    <div className="crm-board-cards">
                      {items.length === 0 ? (
                        <div className="crm-board-empty"><span>Volná fáze</span><small>Přetáhněte sem příležitost</small></div>
                      ) : items.map((lead) => {
                        const language = showLangFilter ? langDef(lead.lang) : null;
                        return (
                          <button
                            key={lead.id}
                            type="button"
                            className={`crm-board-card${dragId === lead.id ? ' dragging' : ''}`}
                            draggable
                            aria-label={`Otevřít lead ${lead.firstName} ${lead.lastName}`}
                            onDragStart={(event) => { event.dataTransfer.setData('text/plain', String(lead.id)); event.dataTransfer.effectAllowed = 'move'; setDragId(lead.id); }}
                            onDragEnd={() => { setDragId(null); setDropCol(null); }}
                            onClick={() => onOpenDetail(String(lead.id))}
                          >
                            <span className="crm-board-card-top">
                              <span className={`crm-board-kind ${lead.kind}`}>{lead.kind === 'inzerent' ? 'Inzerent' : 'Affiliate'}</span>
                              {language ? <span className="crm-board-lang" title={language.fullName}>{language.flag} {language.label}</span> : null}
                              <time dateTime={lead.createdAt}>{fmtDate(lead.createdAt)}</time>
                            </span>
                            <span className="crm-board-card-person">
                              <i>{initials(lead.firstName, lead.lastName)}</i>
                              <span><strong>{lead.firstName} {lead.lastName}</strong><small>{lead.company || lead.email || 'Bez společnosti'}</small></span>
                            </span>
                            <span className="crm-board-card-foot">
                              <span className={lead.assignedToName ? '' : 'unassigned'}>{lead.assignedToName || 'Bez vlastníka'}</span>
                              <strong>{lead.expectedMonthlyEur ? money(lead.expectedMonthlyEur, spaceSlug) : '—'}<small>/ měsíc{lead.expectedMonthlyEur && moneyAlt(lead.expectedMonthlyEur, spaceSlug) ? ` · ${moneyAlt(lead.expectedMonthlyEur, spaceSlug)}` : ''}</small></strong>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        </>
      )}
    </ArcPage>
  );
}
